import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import {
  useCalcTaxesFreightInvoicePurchases,
  useCalcTaxesPurchases,
  usePurchaseAuthorize,
  usePurchaseCancel,
} from '@/core/domain/purchase/Purchase.hooks';
import {
  Purchase,
  PurchaseAuthorizeRequest,
} from '@/core/domain/purchase/Purchase.types';
import { useDisclosure } from '@/hooks';
import { Button, Col, Divider, Modal, Row, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import PurchasesButtonsRules from './PurchasesButtonsRules';
import { ProductPriceChangeOnPurchase } from '@/types/Register';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import PurchaseSendEmailModal from './PurchaseSendEmailModal';
import useFileLoading from '@/hooks/useFileLoading';
import { Status } from '@/utils/enum/statusEnum';
import ButtonBack from '@/components/_common/ButtonBack';
import { useReleasePurchaseFormContext } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';
import { useCompanySettings } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useInvoices } from '@/core/domain/invoice/invoice/invoice.hooks';
import { InvoiceOriginReferenceEntity } from '@/core/domain/invoice/invoices/invoices.enums';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import ReferencedNotesModal from '@/components/_common/ReferencedNotesModal';
import ReferencedNotesButton from '@/components/_common/ReferencedNotesButton';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import PurchasesPrintModal from './PurchasesPrintModal';
import PurchaseSendBoschXml from './PurchaseSendBoschXml';
import PurchaseDownloadXml from './PurchaseDownloadXml';
import PurchasesCancelModal from './PurchasesCancelModal';
import PurchaseUploadXml from './PurchaseUploadXml';
import PurchaseItemsAlert from './PurchaseItemsAlert';
import PurchasesButtonsPurchaseReturn from './PurchasesButtonsPurchaseReturn';

interface Props {
  purchase?: Purchase;
}

const PurchasesButtonsRepeated: React.FC<Props> = ({ purchase }) => {
  const intl = useIntl();
  const { Text } = Typography;
  const history = useHistory();

  const { disabled, purchaseReadOnly, itemsPurchase } =
    useReleasePurchaseFormContext();

  const [modalChangePrice, setModalChangePriceAction] = useDisclosure();
  const [modalEmailVisible, modalEmailAction] = useDisclosure();
  const [modalPurchasesPrintVisible, modalPurchasesPrintAction] =
    useDisclosure();
  const [modalReferencedNotesVisible, modalReferencedNotesAction] =
    useDisclosure();
  const [modalPurchasesCancelVisible, modalPurchasesCancelAction] =
    useDisclosure();

  const { data: matrixSettings } = useMatrix();

  const calcTaxesMutation = useCalcTaxesPurchases();
  const calcTaxesFreightInvoiceMutation = useCalcTaxesFreightInvoicePurchases();
  const purchaseAuthorizeChangeTrue = usePurchaseAuthorize();
  const purchaseAuthorizeChangeFalse = usePurchaseAuthorize();
  const removeMutation = usePurchaseCancel();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const { data: companySettings } = useCompanySettings(purchase?.companyId);
  const { data: invoicesData } = useInvoices({
    entity: InvoiceOriginReferenceEntity.PURCHASE,
    entityId: purchase?.id,
  });

  const invoices = flatInfiniteData(invoicesData);

  const newPurchaseRules = [
    !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_ADD]),
  ];

  const sendEmailRules = [
    !!purchase?.situation && purchase?.situation === Status.canceled,
    !hasPermissions([
      Authorities.MOVIMENTATION_PURCHASE_SEND_PURCHASE_ORDER_BY_EMAIL,
    ]),
  ];

  const authorizePurchaseRules = [
    (!!purchase?.situation && purchase?.situation === Status.authorized) ||
      purchase?.situation === Status.canceled,
    !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_AUTHORIZE]),
  ];

  const cancelPurchaseRules = [
    !!purchase?.situation && purchase?.situation === Status.canceled,
    !!purchase?.situation && purchase?.situation === Status.open,
    !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_CANCEL]),
  ];

  const printOutRules = [
    !!purchase?.situation && purchase?.situation === Status.canceled,
    !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_PRINT_PURCHASE_ORDER]),
  ];

  const calculateTaxesRules = [
    !!purchase?.situation && purchase?.situation === Status.canceled,
  ];

  const handlePurchaseAuthorize = async (change: boolean) => {
    try {
      if (purchase) {
        const obj: PurchaseAuthorizeRequest = {
          purchaseId: purchase.id,
          change,
        };
        if (change || purchase?.freightInvoice) {
          const response = await purchaseAuthorizeChangeTrue.mutateAsync(obj);
          history.push(
            `/app/movements/purchases/purchasesReleases/${response.id}/general`,
          );
        } else {
          const response = await purchaseAuthorizeChangeFalse.mutateAsync(obj);
          history.push(
            `/app/movements/purchases/purchasesReleases/${response.id}/general`,
          );
        }

        setModalChangePriceAction.close();
      }
    } catch {}
  };

  const handleCalculateTaxes = async () => {
    if (purchase) {
      if (purchase?.freightInvoice) {
        await calcTaxesFreightInvoiceMutation.mutateAsync({
          freightBaseIcms: purchase.freightBaseIcms,
          freightQuotaIcms: purchase.freightQuotaIcms,
          purchaseId: purchase.id,
        });
      } else {
        await calcTaxesMutation.mutateAsync(purchase.id);
      }
    }
  };

  const { getSearch } = usePageSearchHistory();
  const path = '/app/movements/purchases/purchasesReleases';
  const pathWithSearch = path + getSearch(path);

  const hasItemsWithoutPartId =
    Boolean(purchase?.proPartsComplementId) &&
    itemsPurchase?.some((item) => !item.partId);

  return (
    <>
      <PurchaseItemsAlert />

      <Divider />

      <Row gutter={[12, 12]}>
        <Col span={24} md={12} lg={6} xxl={3}>
          <ButtonBack path={pathWithSearch} disabled={disabled} />
        </Col>

        {!newPurchaseRules.some((cur) => cur === true) && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsRules
              type="primary"
              onClick={() =>
                history.push(
                  '/app/movements/purchases/purchasesReleases/purchasesCreate',
                )
              }
              intl="purchases.newPurchase"
            />
          </Col>
        )}

        {!sendEmailRules.some((cur) => cur === true) && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsRules
              onClick={modalEmailAction.open}
              intl="purchases.sendEmail"
            />
          </Col>
        )}

        {!authorizePurchaseRules.some((cur) => cur === true) && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsRules
              type="primary"
              onClick={() =>
                matrixSettings?.matrixSettings.productPriceChangeOnPurchase ===
                  ProductPriceChangeOnPurchase.askBeforeChange &&
                !purchase?.freightInvoice
                  ? setModalChangePriceAction.open()
                  : handlePurchaseAuthorize(
                      matrixSettings?.matrixSettings
                        .productPriceChangeOnPurchase ===
                        ProductPriceChangeOnPurchase.alwaysChange,
                    )
              }
              intl="purchases.authorizePurchase"
              loading={
                purchaseAuthorizeChangeTrue.isLoading ||
                purchaseAuthorizeChangeFalse.isLoading
              }
              disabled={purchaseReadOnly || hasItemsWithoutPartId}
            />
          </Col>
        )}

        {invoices.length > 0 && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <ReferencedNotesButton
              onClick={modalReferencedNotesAction.open}
              type="default"
              loading={
                purchaseAuthorizeChangeTrue.isLoading ||
                purchaseAuthorizeChangeFalse.isLoading
              }
            />
          </Col>
        )}

        {!cancelPurchaseRules.some((cur) => cur === true) && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsRules
              danger
              onClick={modalPurchasesCancelAction.open}
              loading={removeMutation.isLoading}
              intl="purchases.cancelPurchase"
              disabled={purchaseReadOnly}
            />
          </Col>
        )}

        {!printOutRules.some((cur) => cur === true) && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsRules
              onClick={modalPurchasesPrintAction.open}
              key="viewReport"
              loading={fileLoading.REPORT_VIEW}
              intl="purchases.printOut"
            />
          </Col>
        )}

        {!calculateTaxesRules.some((cur) => cur === true) && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsRules
              type="primary"
              loading={
                calcTaxesMutation.isLoading ||
                calcTaxesFreightInvoiceMutation.isLoading
              }
              onClick={handleCalculateTaxes}
              intl="purchases.calculateTaxes"
              disabled={purchaseReadOnly}
            />
          </Col>
        )}

        {purchase?.id && (
          <Col span={24} md={12} lg={6} xxl={3}>
            <PurchasesButtonsPurchaseReturn purchase={purchase} />
          </Col>
        )}

        <PurchaseSendBoschXml purchase={purchase} />

        <PurchaseDownloadXml
          purchaseId={purchase?.id}
          purchaseCode={purchase?.code}
          hasXml={!!purchase?.xmlPath}
        />

        <PurchaseUploadXml
          hasXml={!!purchase?.xmlPath}
          purchaseId={purchase?.id}
        />
      </Row>

      <Modal
        title={intl.formatMessage({
          id: 'purchases.btn.authorize.confirm.title',
        })}
        open={modalChangePrice}
        okButtonProps={{ style: { display: 'none' } }}
        onCancel={() => setModalChangePriceAction.toggle()}
        width={750}
        key="modalAuthorize"
        footer={
          <SpaceForButton justifyContent="end">
            <Button
              block
              type="primary"
              ghost
              onClick={() => setModalChangePriceAction.toggle()}
            >
              {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
            </Button>

            <Button
              block
              loading={purchaseAuthorizeChangeTrue.isLoading}
              type="primary"
              onClick={() => handlePurchaseAuthorize(true)}
            >
              {intl.formatMessage({
                id: 'purchases.btn.authorize.modal.authorizeChange',
              })}
            </Button>

            <Button
              block
              loading={purchaseAuthorizeChangeFalse.isLoading}
              type="primary"
              onClick={() => handlePurchaseAuthorize(false)}
            >
              {intl.formatMessage({
                id: 'purchases.btn.authorize.modal.authorize',
              })}
            </Button>
          </SpaceForButton>
        }
      >
        <Row gutter={[24, 24]}>
          <Text>
            {intl.formatMessage({
              id: 'purchases.btn.authorize.confirm.content',
            })}
          </Text>
        </Row>
      </Modal>

      <ReferencedNotesModal
        invoices={invoices}
        open={modalReferencedNotesVisible}
        onCancel={modalReferencedNotesAction.toggle}
      />

      <PurchaseSendEmailModal
        purchase={purchase}
        open={modalEmailVisible}
        onCancel={modalEmailAction.close}
      />

      <PurchasesPrintModal
        open={modalPurchasesPrintVisible}
        onCancel={modalPurchasesPrintAction.close}
        setFileLoading={setFileLoading}
        setFileUnloading={setFileUnloading}
        purchaseId={purchase?.id}
      />

      <PurchasesCancelModal
        open={modalPurchasesCancelVisible}
        onCancel={modalPurchasesCancelAction.close}
        purchaseId={purchase?.id}
        shouldProvideCancellationReasonInPurchase={
          companySettings?.shouldProvideCancellationReasonInPurchase
        }
      />
    </>
  );
};

export default PurchasesButtonsRepeated;
